/**
 * KeyVault — Client-side encrypted key management
 * 
 * This is the core of v3 non-custodial architecture.
 * Private keys are encrypted with AES-256-GCM using a key derived
 * from the user's vault password via PBKDF2.
 * 
 * Keys NEVER leave the browser. The server only stores public info.
 * 
 * Usage:
 *   import keyVault from '@/crypto/KeyVault';
 *   await keyVault.initialize('user-password');
 *   keyVault.storeKey('xrp', 'rAddress...', privateKeyHex);
 *   const key = keyVault.getKey('xrp', 'rAddress...');
 */

const STORAGE_KEY = 'xrpzip_keyvault_v3';
const PBKDF2_ITERATIONS = 100000;
const AUTO_LOCK_MS = 30 * 60 * 1000; // 30 minutes

class KeyVault {
  constructor() {
    this._keys = new Map();          // chain:address → privateKeyHex
    this._encryptionKey = null;      // AES-256-GCM CryptoKey
    this._locked = true;
    this._autoLockTimer = null;
    this._initialized = false;
  }

  // ==================== INITIALIZATION ====================

  /**
   * Check if vault has been set up (has encrypted data in storage)
   */
  isSetUp() {
    try {
      return !!localStorage.getItem(STORAGE_KEY);
    } catch {
      return false;
    }
  }

  /**
   * Check if vault is currently unlocked
   */
  isUnlocked() {
    return !this._locked && this._encryptionKey !== null;
  }

  /**
   * Initialize vault for first time — sets password and creates empty vault
   */
  async setup(password) {
    if (!password || password.length < 6) {
      throw new Error('Vault password must be at least 6 characters');
    }

    const salt = crypto.getRandomValues(new Uint8Array(16));
    this._encryptionKey = await this._deriveKey(password, salt);
    this._locked = false;
    this._keys = new Map();
    this._initialized = true;

    // Save salt and empty encrypted vault
    await this._persist(salt);
    this._resetAutoLock();

    return true;
  }

  /**
   * Unlock vault with password — decrypts stored keys into memory
   */
  async unlock(password) {
    const stored = this._loadStored();
    if (!stored) {
      throw new Error('No vault found. Please set up KeyVault first.');
    }

    const salt = new Uint8Array(stored.salt);
    this._encryptionKey = await this._deriveKey(password, salt);

    try {
      const decrypted = await this._decrypt(stored.data, stored.iv);
      const keyData = JSON.parse(decrypted);

      this._keys = new Map(Object.entries(keyData));
      this._locked = false;
      this._initialized = true;
      this._resetAutoLock();

      return true;
    } catch (e) {
      this._encryptionKey = null;
      throw new Error('Invalid vault password');
    }
  }

  /**
   * Lock vault — clears keys from memory
   */
  lock() {
    this._keys.clear();
    this._encryptionKey = null;
    this._locked = true;
    if (this._autoLockTimer) {
      clearTimeout(this._autoLockTimer);
      this._autoLockTimer = null;
    }
  }

  // ==================== KEY MANAGEMENT ====================

  /**
   * Store a private key
   * @param {string} chain - Chain identifier (xrp, sol, eth, etc.)
   * @param {string} address - Wallet address
   * @param {string} privateKey - Private key hex string
   */
  async storeKey(chain, address, privateKey) {
    this._requireUnlocked();

    const key = `${chain}:${address}`;
    this._keys.set(key, privateKey);

    await this._persistCurrent();
    this._resetAutoLock();
  }

  /**
   * Get a private key
   * @param {string} chain - Chain identifier
   * @param {string} address - Wallet address
   * @returns {string|null} Private key hex or null if not found
   */
  getKey(chain, address) {
    this._requireUnlocked();
    this._resetAutoLock();
    return this._keys.get(`${chain}:${address}`) || null;
  }

  /**
   * Check if a key exists for the given wallet
   */
  hasKey(chain, address) {
    if (this._locked) return false;
    return this._keys.has(`${chain}:${address}`);
  }

  /**
   * Remove a key
   */
  async removeKey(chain, address) {
    this._requireUnlocked();
    this._keys.delete(`${chain}:${address}`);
    await this._persistCurrent();
  }

  /**
   * Get all stored wallet identifiers (no private keys exposed)
   * @returns {Array<{chain: string, address: string}>}
   */
  listKeys() {
    this._requireUnlocked();
    return Array.from(this._keys.keys()).map(k => {
      const [chain, address] = k.split(':');
      return { chain, address };
    });
  }

  /**
   * Change vault password
   */
  async changePassword(oldPassword, newPassword) {
    // Verify old password by trying to unlock
    const stored = this._loadStored();
    if (!stored) throw new Error('No vault found');

    const oldSalt = new Uint8Array(stored.salt);
    const oldKey = await this._deriveKey(oldPassword, oldSalt);

    try {
      await this._decryptWith(oldKey, stored.data, stored.iv);
    } catch {
      throw new Error('Current password is incorrect');
    }

    // Re-encrypt with new password
    const newSalt = crypto.getRandomValues(new Uint8Array(16));
    this._encryptionKey = await this._deriveKey(newPassword, newSalt);
    await this._persist(newSalt);
  }

  /**
   * Export vault data (encrypted) for backup
   */
  exportBackup() {
    const stored = this._loadStored();
    if (!stored) throw new Error('No vault data to export');
    return JSON.stringify(stored);
  }

  /**
   * Import vault data from backup
   */
  importBackup(backupJson) {
    try {
      const data = JSON.parse(backupJson);
      if (!data.salt || !data.data || !data.iv) {
        throw new Error('Invalid backup format');
      }
      localStorage.setItem(STORAGE_KEY, backupJson);
      return true;
    } catch (e) {
      throw new Error('Invalid backup data: ' + e.message);
    }
  }

  /**
   * Destroy vault — permanently deletes all keys
   */
  destroy() {
    this.lock();
    localStorage.removeItem(STORAGE_KEY);
    this._initialized = false;
  }

  // ==================== CRYPTO INTERNALS ====================

  async _deriveKey(password, salt) {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  async _encrypt(plaintext) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const enc = new TextEncoder();
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      this._encryptionKey,
      enc.encode(plaintext)
    );
    return { data: Array.from(new Uint8Array(ciphertext)), iv: Array.from(iv) };
  }

  async _decrypt(data, iv) {
    const ciphertext = new Uint8Array(data);
    const ivBytes = new Uint8Array(iv);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes },
      this._encryptionKey,
      ciphertext
    );
    return new TextDecoder().decode(decrypted);
  }

  async _decryptWith(key, data, iv) {
    const ciphertext = new Uint8Array(data);
    const ivBytes = new Uint8Array(iv);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes },
      key,
      ciphertext
    );
    return new TextDecoder().decode(decrypted);
  }

  async _persist(salt) {
    const keyData = Object.fromEntries(this._keys);
    const encrypted = await this._encrypt(JSON.stringify(keyData));

    const stored = {
      version: 3,
      salt: Array.from(salt),
      data: encrypted.data,
      iv: encrypted.iv,
      updated: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  }

  async _persistCurrent() {
    const stored = this._loadStored();
    if (!stored) throw new Error('No vault to persist to');
    await this._persist(new Uint8Array(stored.salt));
  }

  _loadStored() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  _requireUnlocked() {
    if (this._locked) {
      throw new Error('KeyVault is locked. Please unlock with your password.');
    }
  }

  _resetAutoLock() {
    if (this._autoLockTimer) clearTimeout(this._autoLockTimer);
    this._autoLockTimer = setTimeout(() => {
      this.lock();
      console.info('[KeyVault] Auto-locked after inactivity');
    }, AUTO_LOCK_MS);
  }
}

// Singleton instance
const keyVault = new KeyVault();
export default keyVault;
